"use client";

import { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { DialogTopic } from "@/lib/dialog-data";
import {
  ChevronRight, ChevronDown, Sparkles, Plus, BookOpen, ImageIcon, Clock,
  UtensilsCrossed, Car, ShoppingBag, Heart, Briefcase, Home,
  Users, Plane, Wrench, GraduationCap, MessageCircle, Folder, Library
} from "lucide-react";
import { CurriculumBrowser } from "@/components/curriculum-browser";
import type { CurriculumTopicMeta } from "@/lib/curriculum";
import { useTranslations } from "@/lib/use-translations";
import { useSettings } from "@/lib/settings-context";
import { useLanguage } from "@/lib/language-context";
import api, { type LocaleCode } from "@/lib/api/client";
import { getCategoryName, getCategoryInfo, type CategoryId } from "@/data/dialogue-categories";
import { useAnalytics } from "@/lib/use-analytics";

// Map category IDs to Lucide icons
const CATEGORY_ICONS: Record<CategoryId, React.ComponentType<{ className?: string }>> = {
  food: UtensilsCrossed,
  transport: Car,
  shopping: ShoppingBag,
  health: Heart,
  work: Briefcase,
  home: Home,
  social: Users,
  travel: Plane,
  services: Wrench,
  education: GraduationCap,
  other: MessageCircle,
};

// Extended topic type with regeneration flag
interface TopicWithMeta extends DialogTopic {
  _needsRegeneration?: boolean;
  _generatedFor?: {
    targetLanguage: LocaleCode;
    nativeLanguage: LocaleCode;
  };
}

interface TopicSelectorProps {
  topics: TopicWithMeta[];
  onSelectTopic: (topic: DialogTopic) => void;
  onAdaptTopic?: (topic: CurriculumTopicMeta) => void;
  onCreateCustom?: () => void;
  onOpenSettings?: () => void;
}

// Topic card with background image
function TopicCard({
  topic,
  index,
  debugMode,
  onSelect,
  proficiencyStyles,
  translations,
}: {
  topic: TopicWithMeta;
  index: number;
  debugMode: boolean;
  onSelect: () => void;
  proficiencyStyles: { badge: string; dot: string };
  translations: ReturnType<typeof useTranslations>;
}) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    async function loadImage() {
      if (!topic.title || !topic.description) {
        setImageLoading(false);
        return;
      }

      setImageLoading(true);
      setImageError(false);

      try {
        const { response } = await api.GET("/api/topic-image", {
          params: {
            query: {
              id: topic.id,
              title: topic.title,
              description: topic.description,
              language: topic.language,
            },
          },
          parseAs: "stream",
        });

        if (!response.ok) {
          throw new Error("Failed to load image");
        }

        const blob = await response.blob();

        if (!cancelled) {
          objectUrl = URL.createObjectURL(blob);
          setImageUrl(objectUrl);
        }
      } catch {
        if (!cancelled) {
          setImageError(true);
        }
      } finally {
        if (!cancelled) {
          setImageLoading(false);
        }
      }
    }

    loadImage();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [topic.id, topic.title, topic.description, topic.language]);

  const t = translations;

  return (
    <Card
      className="card-interactive cursor-pointer border-border/60 bg-card/80 backdrop-blur-sm hover:border-primary/40 animate-fade-up overflow-hidden group relative min-h-[120px]"
      style={{ animationDelay: `${index * 60}ms` }}
      onClick={onSelect}
    >
      {/* Background image */}
      {imageUrl && (
        <div
          className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
          style={{ backgroundImage: `url(${imageUrl})` }}
        >
          {/* Gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-card/95 via-card/90 to-card/80 dark:from-card/98 dark:via-card/92 dark:to-card/82" />
        </div>
      )}

      {/* Loading state for image */}
      {imageLoading && !imageError && (
        <div className="absolute top-3 right-3 z-10">
          <div className="w-6 h-6 rounded-full bg-muted/80 flex items-center justify-center animate-pulse">
            <ImageIcon className="w-3 h-3 text-muted-foreground" />
          </div>
        </div>
      )}

      {/* Decorative accent line */}
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-primary via-primary/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" />

      <CardHeader className="pb-3 pl-5 relative z-10">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            {/* Category badge */}
            {topic.category && (
              <div className="flex items-center gap-1.5 mb-1.5">
                {(() => {
                  const catInfo = getCategoryInfo(topic.category as CategoryId);
                  const Icon = CATEGORY_ICONS[topic.category as CategoryId] || MessageCircle;
                  return (
                    <span className={`flex items-center gap-1 text-xs ${catInfo.color}`}>
                      <Icon className="w-3 h-3" />
                    </span>
                  );
                })()}
              </div>
            )}
            <CardTitle className="text-base font-medium text-card-foreground leading-snug mb-1.5 drop-shadow-sm line-clamp-2">
              {topic.summary || topic.title}
            </CardTitle>
            <CardDescription className="text-sm line-clamp-2 leading-relaxed drop-shadow-sm">
              {topic.description}
            </CardDescription>
          </div>
          <div className="w-8 h-8 rounded-full bg-card/80 backdrop-blur-sm flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-all duration-300 shadow-sm">
            <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all duration-300" />
          </div>
        </div>
      </CardHeader>

      {debugMode && (
        <CardContent className="pt-0 pb-4 pl-5 relative z-10">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge
              variant="outline"
              className="text-xs font-normal bg-card/80 backdrop-blur-sm"
            >
              {topic.language}
            </Badge>
            <Badge
              variant="outline"
              className={`text-xs font-normal backdrop-blur-sm ${proficiencyStyles.badge}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${proficiencyStyles.dot} mr-1.5`} />
              {t.topics.difficulty[topic.difficulty as keyof typeof t.topics.difficulty] || topic.difficulty}
            </Badge>
            <span className="text-xs text-muted-foreground ml-auto flex items-center gap-1.5 bg-card/80 backdrop-blur-sm px-2 py-0.5 rounded">
              <BookOpen className="w-3 h-3" />
              {topic.vocabulary.words.length} {t.topics.words}
            </span>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

type TopicSubTab = "my-topics" | "curriculum";

export function TopicSelector({ topics, onSelectTopic, onAdaptTopic, onCreateCustom, onOpenSettings }: TopicSelectorProps) {
  const t = useTranslations();
  const { debugMode } = useSettings();
  const { targetLanguage, nativeLanguage } = useLanguage();
  const { track, events } = useAnalytics();
  const [subTab, setSubTab] = useState<TopicSubTab>("my-topics");

  // Wrap onSelectTopic to track the event
  const handleSelectTopic = useCallback((topic: DialogTopic) => {
    track(events.TOPIC_SELECTED, {
      topic_id: topic.id,
      title: topic.title,
      difficulty: topic.difficulty,
      category: topic.category || "other",
      is_custom: !!(topic as TopicWithMeta)._generatedFor,
    });
    onSelectTopic(topic);
  }, [track, events, onSelectTopic]);

  // Track which category folders are expanded
  const [expandedCategories, setExpandedCategories] = useState<Set<CategoryId>>(new Set());

  const toggleCategory = useCallback((categoryId: CategoryId) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      const isExpanding = !next.has(categoryId);
      if (isExpanding) {
        next.add(categoryId);
        // Scroll to show expanded content after animation starts
        setTimeout(() => {
          const element = document.getElementById(`category-${categoryId}`);
          element?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 50);
      } else {
        next.delete(categoryId);
      }
      return next;
    });
  }, []);

  // Filter out topics generated for a different language (unless debug mode)
  const filteredTopics = debugMode
    ? topics
    : topics.filter((topic) => {
        const generatedFor = topic._generatedFor;
        if (!generatedFor) return true; // Show topics without language info
        // Compare codes, not names (e.g., "pt-PT" not "Portuguese")
        return (
          generatedFor.targetLanguage === targetLanguage.code &&
          generatedFor.nativeLanguage === nativeLanguage.code
        );
      });

  const [showAllRecent, setShowAllRecent] = useState(false);
  const RECENT_PREVIEW_COUNT = 3;

  // Group topics: Recent (all generated, newest first) + by category (all topics)
  const { recentTopics, categorizedTopics } = useMemo(() => {
    // All generated topics, newest first (they're prepended to the array)
    const allRecent = filteredTopics.filter(t => t._generatedFor);

    // Group ALL topics by category (including generated ones)
    const byCategory: Record<CategoryId, TopicWithMeta[]> = {
      food: [], transport: [], shopping: [], health: [], work: [],
      home: [], social: [], travel: [], services: [], education: [], other: []
    };

    for (const topic of filteredTopics) {
      const cat = (topic.category as CategoryId) || "other";
      byCategory[cat].push(topic);
    }

    // Filter out empty categories
    const nonEmpty = Object.entries(byCategory)
      .filter(([, topics]) => topics.length > 0) as [CategoryId, TopicWithMeta[]][];

    return { recentTopics: allRecent, categorizedTopics: nonEmpty };
  }, [filteredTopics]);

  const visibleRecent = showAllRecent
    ? recentTopics
    : recentTopics.slice(0, RECENT_PREVIEW_COUNT);
  const hasMoreRecent = recentTopics.length > RECENT_PREVIEW_COUNT;

  const getProficiencyStyles = (proficiency: string) => {
    switch (proficiency) {
      case "beginner":
        return {
          badge: "bg-success/15 text-success border-success/30 hover:bg-success/20",
          dot: "bg-success"
        };
      case "intermediate":
        return {
          badge: "bg-primary/15 text-primary border-primary/30 hover:bg-primary/20",
          dot: "bg-primary"
        };
      case "advanced":
        return {
          badge: "bg-accent/15 text-accent border-accent/30 hover:bg-accent/20",
          dot: "bg-accent"
        };
      default:
        return {
          badge: "bg-muted text-muted-foreground border-muted-foreground/20",
          dot: "bg-muted-foreground"
        };
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* Header with glass effect */}
      <header className="sticky top-0 z-10 glass shadow-[0_1px_8px_rgba(0,0,0,0.08)] dark:shadow-[0_1px_8px_rgba(0,0,0,0.4)]">
        <div className="px-5 py-4 flex items-center gap-3">
          {/* App icon */}
          <img
            src="/app-icon.png"
            alt="SaySay"
            className="w-10 h-10 rounded-xl shadow-md"
          />

          {/* Title */}
          <div className="flex-1">
            <h1 className="text-xl font-semibold tracking-tight">SaySay</h1>
          </div>

          {onOpenSettings && (
            <button
              type="button"
              onClick={onOpenSettings}
              className="w-10 h-10 rounded-xl hover:bg-muted/80 flex items-center justify-center transition-colors text-2xl"
            >
              {targetLanguage.flag}
            </button>
          )}
        </div>
      </header>

      {/* Hero Section with decorative pattern */}
      <section className="relative px-5 py-8 overflow-hidden">
        {/* Decorative background */}
        <div className="absolute inset-0 pattern-tile opacity-50" />
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-accent/10 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-primary/8 to-transparent rounded-full blur-2xl" />

        {/* Content */}
        <div className="relative">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-10 h-10 rounded-lg bg-accent/15 flex items-center justify-center shrink-0 mt-0.5">
              <Sparkles className="w-5 h-5 text-accent" />
            </div>
            <div>
              <h2 className="text-2xl font-display text-foreground leading-tight mb-2">
                {t.topics.heroTitle}
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-md">
                {t.topics.heroSubtitle}
              </p>
            </div>
          </div>

          {onCreateCustom && (
            <Button
              onClick={onCreateCustom}
              variant="outline"
              className="w-full h-14 text-base gap-3 border-dashed border-2 bg-card/50 hover:bg-card hover:border-primary/50 hover:text-primary transition-all duration-300 group"
            >
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/15 transition-colors">
                <Plus className="w-4 h-4 text-primary" />
              </div>
              {t.topics.createCustom}
            </Button>
          )}
        </div>
      </section>

      {/* Sub-tab toggle: My Topics / Curriculum */}
      <div className="relative px-5 pb-2">
        <div className="relative flex gap-1 p-1 rounded-xl bg-muted/50 border border-border/40">
          <button
            type="button"
            onClick={() => setSubTab("my-topics")}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
              subTab === "my-topics"
                ? "bg-card shadow-sm text-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Sparkles className="w-4 h-4" />
            {t.topics.recent || "My Topics"}
          </button>
          <button
            type="button"
            onClick={() => setSubTab("curriculum")}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
              subTab === "curriculum"
                ? "bg-card shadow-sm text-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Library className="w-4 h-4" />
            Curriculum
          </button>
        </div>
      </div>

      {/* Topics Grid (My Topics sub-tab) */}
      {subTab === "my-topics" && (
      <section className="relative flex-1 px-5 py-2 pb-8 overflow-hidden">
        {/* Decorative background */}
        <div className="absolute inset-0 pattern-tile opacity-50" />
        <div className="absolute top-1/4 right-0 w-48 h-48 bg-gradient-to-bl from-accent/5 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-0 w-40 h-40 bg-gradient-to-tr from-primary/5 to-transparent rounded-full blur-2xl" />

        {/* Recent section */}
        {recentTopics.length > 0 && (
          <div className="relative mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <h3 className="text-sm font-medium text-muted-foreground">{t.topics.recent || "Recent"}</h3>
            </div>
            <div className="flex flex-col gap-3">
              {visibleRecent.map((topic, index) => (
                <TopicCard
                  key={topic.id}
                  topic={topic}
                  index={index}
                  debugMode={debugMode}
                  onSelect={() => handleSelectTopic(topic)}
                  proficiencyStyles={getProficiencyStyles(topic.difficulty)}
                  translations={t}
                />
              ))}
            </div>
            {hasMoreRecent && (
              <button
                type="button"
                onClick={() => setShowAllRecent(!showAllRecent)}
                className="mt-3 w-full py-2.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors rounded-lg hover:bg-primary/5"
              >
                {showAllRecent
                  ? (t.topics.collapse || "Show less")
                  : `${t.topics.recent || "Show all"} (${recentTopics.length})`}
              </button>
            )}
          </div>
        )}

        {/* Category collapsibles */}
        {categorizedTopics.length > 0 && (
          <div className="relative mb-6 space-y-3">
            <div className="flex items-center gap-2 mb-3">
              <Folder className="w-4 h-4 text-muted-foreground" />
              <h3 className="text-sm font-medium text-muted-foreground">{t.topics.byCategory || "Dialogues by category"}</h3>
            </div>
            {categorizedTopics.map(([categoryId, categoryTopics]) => {
              const catInfo = getCategoryInfo(categoryId);
              const Icon = CATEGORY_ICONS[categoryId] || MessageCircle;
              const categoryName = getCategoryName(categoryId, nativeLanguage.code);
              const isExpanded = expandedCategories.has(categoryId);

              return (
                <div key={categoryId} id={`category-${categoryId}`} className="rounded-xl overflow-hidden scroll-mt-4">
                  {/* Collapsible header - styled like original folder tiles */}
                  <button
                    type="button"
                    onClick={() => toggleCategory(categoryId)}
                    className={`w-full flex items-center gap-3 p-4 rounded-xl border transition-all duration-200 text-left ${
                      isExpanded
                        ? "bg-primary/5 border-primary/30 shadow-sm"
                        : "bg-card/80 border-border/60 hover:border-primary/30 hover:bg-card"
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isExpanded ? "bg-primary/15" : "bg-muted/50"
                    }`}>
                      <Icon className={`w-5 h-5 ${catInfo.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{categoryName}</div>
                      <div className="text-xs text-muted-foreground">
                        {categoryTopics.length} {categoryTopics.length === 1 ? "dialogue" : "dialogues"}
                      </div>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${
                      isExpanded ? "rotate-180" : ""
                    }`} />
                  </button>

                  {/* Expanded content */}
                  {isExpanded && (
                    <div className="pt-3 space-y-3 animate-fade-up">
                      {categoryTopics.map((topic, index) => (
                        <TopicCard
                          key={topic.id}
                          topic={topic}
                          index={index}
                          debugMode={debugMode}
                          onSelect={() => handleSelectTopic(topic)}
                          proficiencyStyles={getProficiencyStyles(topic.difficulty)}
                          translations={t}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Empty state */}
        {filteredTopics.length === 0 && (
          <div className="relative flex flex-col items-center justify-center py-16 text-center animate-fade-up">
            <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mb-4">
              <BookOpen className="w-7 h-7 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground text-sm max-w-xs">
              No topics available yet. Create a custom topic to get started.
            </p>
          </div>
        )}
      </section>
      )}

      {/* Curriculum sub-tab */}
      {subTab === "curriculum" && (
        <section className="relative flex-1 px-5 py-4 pb-8">
          <CurriculumBrowser onAdaptTopic={onAdaptTopic!} />
        </section>
      )}

      {/* Bottom safe area */}
      <div className="h-6 safe-area-bottom" />
    </div>
  );
}
